import {combineLatest as observableCombineLatest, of as observableOf, Observable} from 'rxjs';

import {
  map,
  mergeMap,
  takeWhile,
  catchError,
  switchMap,
  distinctUntilChanged,
} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone} from '@angular/core';
import {DetailControl, DetailControlOption, ValuesObject} from '../detail-tabconfig';
import {ResourceService} from '../../resource.service';
import {FilterResult} from '../../filterresult';
import {AbstractControlComponent} from './abstract-control';
import {DetailService} from '../detail.service';
import {CacheService} from '../../../cache/cache.service';
import {SupportedLocaleConfiguration} from '../../../cache/uiconfiguration/supportedlocaleconfiguration';
import {ViewTabComponent} from '../viewtab.component';
import {optioncontrolRequired} from './validators/optioncontrol-required-validator';
import {ErrorService} from '../../../error/error.service';
import {DisplayAttribute} from '../../displayattribute';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';

@Component({
  selector: 'ctrl-dropdown',
  template: `
    <mat-form-field floatLabel="never">
      <mat-select
        placeholder="{{ (getCaughtObservable(_config?.Caption) | async)?.Values }}"
        [formControl]="formControl"
        (opened)="onSelectOpen()"
        (closed)="onSelectClose()"
      >
        @for (opt of options; track opt) {
          <span>
            @if (getCaughtObservable(opt.Value) | async) {
              <mat-option
                matTooltipShowDelay="300"
                [matTooltip]="(getCaughtObservable(opt.Hint) | async)?.Values"
                [value]="(getCaughtObservable(opt.Value) | async)?.Values"
              >
                {{ (getCaughtObservable(opt.Caption) | async)?.Values }}
              </mat-option>
            }
          </span>
        }
      </mat-select>
    </mat-form-field>
    @if (
      (getCaughtObservable(isEnabled) | async) &&
      formControl.touched &&
      formControl.hasError('required')
    ) {
      <mat-error>
        <language key="ControlErrorRequired" defaultValue="This field is required."></language>
      </mat-error>
    }
  `,
  styles: [
    `
      mat-select,
      mat-form-field {
        width: 100%;
      } /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-form-field-infix {
        border-top: none;
      } /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-form-field-wrapper {
        padding-bottom: 0;
      } /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-form-field-underline {
        bottom: 0;
      } /* TODO(mdc-migration): The following rule targets internal classes of select that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-select-value {
        vertical-align: middle;
      } /* TODO(mdc-migration): The following rule targets internal classes of select that may no longer apply for the MDC version. */
      :host ::ng-deep .mat-select-trigger {
        height: 20px;
        font-size: inherit;
      }
      :host ::ng-deep .mat-floating-placeholder {
        pointer-events: none;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlDropdownComponent extends AbstractControlComponent {
  constructor(
    parent: ViewTabComponent,
    private resourceService: ResourceService,
    changeDetector: ChangeDetectorRef,
    private cacheService: CacheService,
    _ngZone: NgZone,
    errorService: ErrorService,
    private envConfig: EnvironmentConfigService,
  ) {
    super(parent, 'SelectedValue', 'SelectedValue', changeDetector, _ngZone, errorService);
  }

  options: DetailControlOption[] = [];
  private _objectType: string = null;
  @Input()
  set objectType(objectType: string) {
    this._objectType = objectType;
  }

  get objectType(): string {
    return this._objectType;
  }

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    this._config
      .getBooleanPropertyValues('Required')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(required => {
        if (required === true) {
          this.formControl.setValidators([optioncontrolRequired()]);
        } else {
          this.formControl.clearValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });
    CtrlDropdownComponent.regenerateOptions(
      this._config,
      this.resourceService,
      this.cacheService,
      this.objectType,
      this.envConfig,
    )
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(options => {
        this.options = options;
        this.changeDetector.markForCheck();
      });
  }

  static getAdditionalMVAttributeReferenceObjects(
    resourceService: ResourceService,
    objects: any[],
    attributes: string[],
    captionPath: Observable<string>,
    valuePath: Observable<string>,
    hintPath: Observable<string>,
    caption: string,
    value: string,
    hint: string,
    options: DetailControlOption[],
  ): Observable<DetailControlOption[]> {
    let filter = '/Resource[';
    let objectsToProcess = objects.splice(0, 10);
    objectsToProcess.forEach(value => {
      if (filter === '/Resource[') {
        filter += "ObjectID='" + value + "'";
      } else {
        filter += " or ObjectID='" + value + "'";
      }
    });
    filter += ']';
    return resourceService.searchResources(filter, attributes.join(','), 99999).pipe(
      mergeMap((res: FilterResult) => {
        if (res != null && res.Results != null && Array.isArray(res.Results)) {
          res.Results.forEach(opt => {
            let cfgOpt: DetailControlOption = new DetailControlOption();
            if (captionPath != null)
              cfgOpt.setCaption(observableOf(new ValuesObject(opt[caption])));
            if (valuePath != null) cfgOpt.setValue(observableOf(new ValuesObject(opt[value])));
            if (hintPath != null) cfgOpt.setHint(observableOf(new ValuesObject(opt[hint])));
            options.push(cfgOpt);
          });
        }
        if (objects.length > 0) {
          return this.getAdditionalMVAttributeReferenceObjects(
            resourceService,
            objects,
            attributes,
            captionPath,
            valuePath,
            hintPath,
            caption,
            value,
            hint,
            options,
          );
        } else {
          return observableOf(options);
        }
      }),
    );
  }

  static regenerateOptions(
    config: DetailControl,
    resourceService: ResourceService,
    cacheService: CacheService,
    objectType: string,
    envConfig: EnvironmentConfigService,
  ): Observable<DetailControlOption[]> {
    if (config == null) {
      return observableOf([]);
    }

    let options: DetailControlOption[] = [];

    // The RCDC literal ValuePath="Value" historically resolves to DisplayName. When the control's
    // bound target attribute (Source / SelectedValue with Source=object) is a Reference attribute,
    // the stored value is an ObjectID (GUID), so the option values must be ObjectIDs for
    // pre-selection to match and for saves to write a valid reference.
    let targetPropertyName: string = null;
    if (config.propertyExists('Source')) {
      targetPropertyName = 'Source';
    } else if (config.propertyExists('SelectedValue')) {
      targetPropertyName = 'SelectedValue';
    }

    // Note: objectType may still be null here — [config] is bound before [objectType] in the
    // viewtab template and this method runs synchronously from the config setter. That is fine:
    // getDisplayAttribute resolves DataType from the schema-wide attribute list by system name
    // alone; objectType only refines binding-specific display metadata, which is not used here.
    let isReferenceTarget: Observable<boolean>;
    if (targetPropertyName == null) {
      isReferenceTarget = observableOf(false);
    } else {
      isReferenceTarget = config.getProperty(targetPropertyName).pipe(
        switchMap((target: ValuesObject<any>) => {
          if (
            target == null ||
            target.BindingSource !== 'object' ||
            target.BindingPath == null ||
            target.BindingPath.indexOf('/') !== -1
          ) {
            return observableOf(false);
          }
          return cacheService
            .getDisplayAttribute(target.BindingPath, objectType)
            .pipe(
              map(
                (attribute: DisplayAttribute) =>
                  attribute != null && attribute.DataType === 'Reference',
              ),
            );
        }),
        catchError(() => observableOf(false)),
        distinctUntilChanged(),
      );
    }

    let valuePath: Observable<string> = observableCombineLatest([
      config.getPropertyValues('ValuePath'),
      isReferenceTarget,
    ]).pipe(
      map(([value, referenceTarget]) => {
        if (value === 'Value') {
          return referenceTarget
            ? envConfig.getAttributeName('ObjectID')
            : envConfig.getAttributeName('DisplayName');
        } else {
          return value;
        }
      }),
    );
    let captionPath: Observable<string> = config.getPropertyValues('CaptionPath').pipe(
      map(value => {
        if (value === 'Caption') {
          return envConfig.getAttributeName('DisplayName');
        } else {
          return value;
        }
      }),
    );
    let hintPath: Observable<string> = config.getPropertyValues('HintPath').pipe(
      map(value => {
        if (value === 'Hint') {
          return envConfig.getAttributeName('DisplayName');
        } else {
          return value;
        }
      }),
    );
    return observableCombineLatest(
      config.getProperty('ItemSource'),
      config.getProperty('Filter'),
      valuePath,
      captionPath,
      hintPath,
    ).pipe(
      mergeMap(combinedValues => {
        const itemSource: ValuesObject<any> = combinedValues[0];
        const filter: ValuesObject<any> = combinedValues[1];
        const value = combinedValues[2];
        const caption = combinedValues[3];
        const hint = combinedValues[4];

        if (config == null) {
          return observableOf([]);
        }

        let options: DetailControlOption[] = [];

        if (config.Options != null && config.Options.length > 0) {
          options = config.Options;
        }

        if (filter != null && filter.Values != null) {
          let attributes: string[] = [];
          if (value != null) attributes.push(value);
          if (caption != null) attributes.push(caption);
          if (hint != null) attributes.push(hint);
          return resourceService.searchResources(filter.Values, attributes.join(','), 99999).pipe(
            map((res: FilterResult) => {
              if (res != null && res.Results != null && Array.isArray(res.Results)) {
                res.Results.forEach(opt => {
                  let cfgOpt: DetailControlOption = new DetailControlOption();
                  if (captionPath != null)
                    cfgOpt.setCaption(observableOf(new ValuesObject(opt[caption])));
                  if (valuePath != null)
                    cfgOpt.setValue(observableOf(new ValuesObject(opt[value])));
                  if (hintPath != null) cfgOpt.setHint(observableOf(new ValuesObject(opt[hint])));
                  options.push(cfgOpt);
                });
              }
              return options;
            }),
          ); // .catch((error, caught) => Observable.throwError(new InvalidItemSourceObjectError(itemSource.BindingPath, attributes.join(",")), caught))
        } else if (
          itemSource != null &&
          itemSource.BindingPath != null &&
          itemSource.BindingSource != null &&
          !DetailService.isKnownBinding(itemSource.BindingSource) &&
          !DetailService.isLocaleBinding(itemSource.BindingSource)
        ) {
          let attributes: string[] = [];
          if (value != null) attributes.push(value);
          if (caption != null) attributes.push(caption);
          if (hint != null) attributes.push(hint);

          return resourceService
            .searchResources('/' + itemSource.BindingPath, attributes.join(','), 99999)
            .pipe(
              map((res: FilterResult) => {
                if (res != null && res.Results != null && Array.isArray(res.Results)) {
                  res.Results.forEach(opt => {
                    let cfgOpt: DetailControlOption = new DetailControlOption();
                    if (captionPath != null)
                      cfgOpt.setCaption(observableOf(new ValuesObject(opt[caption])));
                    if (valuePath != null)
                      cfgOpt.setValue(observableOf(new ValuesObject(opt[value])));
                    if (hintPath != null) cfgOpt.setHint(observableOf(new ValuesObject(opt[hint])));
                    options.push(cfgOpt);
                  });
                }
                return options;
              }),
            ); // .catch((error, caught) => Observable.throwError(new InvalidItemSourceObjectError(itemSource.BindingPath, attributes.join(",")), caught))
        } else if (
          itemSource != null &&
          itemSource.BindingPath != null &&
          itemSource.BindingSource != null &&
          DetailService.isLocaleBinding(itemSource.BindingSource)
        ) {
          return cacheService.getSupportedLocaleConfiguration().pipe(
            map((res: SupportedLocaleConfiguration) => {
              res.supportedlanguagecode.forEach(opt => {
                let cfgOpt: DetailControlOption = new DetailControlOption();
                if (captionPath != null) cfgOpt.setCaption(observableOf(new ValuesObject(opt)));
                if (valuePath != null) cfgOpt.setValue(observableOf(new ValuesObject(opt)));
                if (hintPath != null) cfgOpt.setHint(observableOf(new ValuesObject(opt)));
                options.push(cfgOpt);
              });
              return options;
            }),
          );
        } else if (itemSource != null && itemSource.BindingSource === 'schema') {
          itemSource.Values.forEach(value => {
            let cfgOpt: DetailControlOption = new DetailControlOption();
            cfgOpt.setCaption(observableOf(new ValuesObject(value)));
            cfgOpt.setValue(observableOf(new ValuesObject(value)));
            cfgOpt.setHint(observableOf(new ValuesObject(null)));
            options.push(cfgOpt);
          });
          return observableOf(options);
        } else if (
          itemSource != null &&
          itemSource.BindingPath != null &&
          itemSource.BindingSource != null &&
          DetailService.isKnownBinding(itemSource.BindingSource) &&
          Array.isArray(itemSource.Values) &&
          itemSource.Values != undefined &&
          itemSource.Values.length !== 0
        ) {
          const splitterIndex: number = itemSource.BindingPath.indexOf('/');
          let path: string = itemSource.BindingPath;
          if (splitterIndex !== -1) {
            const path1: string = itemSource.BindingPath.substring(0, splitterIndex);
            path = itemSource.BindingPath.substring(splitterIndex + 1);
          }
          // this is for Manager/Locations bindings
          return cacheService.getDisplayAttribute(path, objectType).pipe(
            mergeMap((attribute: DisplayAttribute) => {
              if (attribute != null && attribute.DataType === 'Reference') {
                let attributes: string[] = [];
                if (value != null) attributes.push(value);
                if (caption != null) attributes.push(caption);
                if (hint != null) attributes.push(hint);

                /*let filter = "/Resource[";
                            itemSource.Values.forEach(value => {
                                if (filter === "/Resource[") {
                                    filter += "ObjectID='" + value + "'";
                                } else {
                                    filter += " or ObjectID='" + value + "'";
                                }
                            });
                            filter += "]";

                            return resourceService.searchResources(filter, attributes.join(","), 99999)
                                .map((res: FilterResult) => {
                                    if (res != null && res.Results != null && Array.isArray(res.Results)) {
                                        res.Results.forEach((opt) => {
                                            let cfgOpt: DetailControlOption = new DetailControlOption();
                                            if (captionPath != null) cfgOpt.setCaption(Observable.of(new ValuesObject(opt[caption])));
                                            if (valuePath != null) cfgOpt.setValue(Observable.of(new ValuesObject(opt[value])));
                                            if (hintPath != null) cfgOpt.setHint(Observable.of(new ValuesObject(opt[hint])));
                                            options.push(cfgOpt);
                                        });
                                    }
                                    return options;
                                }); // .catch((error, caught) => this.handleError(new InvalidItemSourceObjectError(itemSource.BindingPath, attributes.join(",")), caught));*/
                return CtrlDropdownComponent.getAdditionalMVAttributeReferenceObjects(
                  resourceService,
                  itemSource.Values,
                  attributes,
                  captionPath,
                  valuePath,
                  hintPath,
                  caption,
                  value,
                  hint,
                  options,
                );
              } else {
                itemSource.Values.forEach(value => {
                  let cfgOpt: DetailControlOption = new DetailControlOption();
                  cfgOpt.setCaption(observableOf(new ValuesObject(value)));
                  cfgOpt.setValue(observableOf(new ValuesObject(value)));
                  cfgOpt.setHint(observableOf(new ValuesObject(null)));
                  options.push(cfgOpt);
                });
                return observableOf(options);
              }
            }),
          );
        } else {
          return observableOf(options);
        }
      }),
    );
  }

  protected formatSource(source: any): any {
    if (source === '' || source == null) source = DetailControlOption.NullValueOverwrite;

    if (Array.isArray(source)) {
      if (source.length === 0) {
        return DetailControlOption.NullValueOverwrite;
      } else {
        return source[0];
      }
    } else {
      return source;
    }
  }

  protected formatTarget(value: any): any {
    if (value === DetailControlOption.NullValueOverwrite) value = null;
    return value;
  }

  onSelectOpen(): void {
    document.body.style.overflow = 'hidden';
  }

  onSelectClose(): void {
    document.body.style.overflow = '';
  }
}
